export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 16;

const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export type PasswordValidationResult = { isValid: boolean; error: string };

export type PasswordChecks = {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
};

export function getPasswordChecks(password: string): PasswordChecks {
  return {
    length: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: SPECIAL_CHAR_REGEX.test(password),
  };
}

export function validatePassword(password: string): PasswordValidationResult {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    };
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Password must be at most ${PASSWORD_MAX_LENGTH} characters long`,
    };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }
  if (!SPECIAL_CHAR_REGEX.test(password)) {
    return { isValid: false, error: 'Password must contain at least one special character' };
  }
  return { isValid: true, error: '' };
}

/** Returns the first validation error message, or undefined when valid. */
export function validateNewPassword(password: string): string | undefined {
  const result = validatePassword(password);
  return result.isValid ? undefined : result.error;
}

export const PASSWORD_RULES: { label: string; met: (password: string) => boolean }[] = [
  {
    label: `${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters`,
    met: (p) => p.length >= PASSWORD_MIN_LENGTH && p.length <= PASSWORD_MAX_LENGTH,
  },
  { label: 'One uppercase letter', met: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', met: (p) => /[a-z]/.test(p) },
  { label: 'One number', met: (p) => /[0-9]/.test(p) },
  { label: 'One special character (!@#$%^&* etc.)', met: (p) => SPECIAL_CHAR_REGEX.test(p) },
];

export const PASSWORD_REQUIREMENTS_HINT =
  `Passwords must be ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters and include uppercase, lowercase, numbers, and special characters.`;
